<script setup lang="ts">
import { computed, useAttrs, useTemplateRef, type InputHTMLAttributes } from 'vue';
import { rearmSearchEscapeGuard, useBrowserAutofillOffAttrs } from './browserAutofill';
import { type AlignedTextInputProps } from './internal';
import NxInputWrapper from './NxInputWrapper.vue';

type InputProps = AlignedTextInputProps & {
  type: 'text' | 'password';
  reservePlainCellActionSpace?: boolean;
  /** Shows the value in capitals (NxTextInput `uppercase`). */
  uppercase?: boolean;
  /**
   * @internal Kit composites that handle Escape themselves (an own edit buffer,
   * a list) set `false`: the field then leaves Escape alone. Not for apps.
   */
  escapeRestores?: boolean;
  /** The value is not valid: error border and `aria-invalid` (see NxInputWrapper). */
  invalid?: boolean;
};

const props = withDefaults(
  defineProps<InputProps & /* @vue-ignore */ Omit<InputHTMLAttributes, 'value'>>(),
  { escapeRestores: true }
);

const [valueModel, valueModelModifiers] = defineModel<string, 'lazy'>({ required: true });

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();
// Inside a provideBrowserAutofillOff() scope a plain text field renders as a
// text-looking `type="search"` so Chromium stops offering to save addresses.
const browserAutofillOffAttrs = useBrowserAutofillOffAttrs();
const autofillAttrs = computed(() => browserAutofillOffAttrs(props.type, attrs.autocomplete));
// A caller's `autocomplete="off"` means the same, but Chromium needs the token.
const autofillWins = computed(() =>
  autofillAttrs.value.autocomplete ? { autocomplete: autofillAttrs.value.autocomplete } : {}
);

const inputRef = useTemplateRef<HTMLInputElement>('input-element');
// The wrapper gets the field's props, but not the internal Escape switch, not
// the value's `align` and `uppercase` (this input's own classes) and not the
// model (`modelValue` / `modelModifiers`, props through defineModel): whatever
// the wrapper does not declare lands on the native input as an attribute
// (`modelvalue="…"`, `modelmodifiers="[object Object]"`, `align="end"`).
const wrapperProps = computed(() => {
  const rest: Record<string, unknown> = { ...props };
  delete rest.modelValue;
  delete rest.modelModifiers;
  delete rest.align;
  delete rest.uppercase;
  return { ...rest, escapeRestores: undefined };
});
const inputClass = computed(() => [
  `height-${props.height ?? 's'}`,
  { 'align-end': props.align === 'end', uppercase: props.uppercase },
]);

/**
 * The model value before the first edit since the field got focus (plain
 * model only). Taken at the first keydown or `beforeinput` after focus rather
 * than at the focus itself: a composite that swaps its display for an edit
 * buffer on focus (amount, masked value) has rendered that buffer by then, so
 * the restore puts back the buffer, not the formatted display. `null` = nothing
 * edited since focus. A `change` (Enter, blur) commits and moves the baseline.
 */
let editStart: string | null = null;

function onFocus() {
  editStart = null;
}
function rememberEditStart(event: Event) {
  if (editStart !== null) return;
  if (event instanceof KeyboardEvent && event.key === 'Escape') return;
  editStart = valueModel.value ?? '';
}
function onChange() {
  editStart = null;
}

/**
 * Escape discards the typing since the field got focus. Runs after the
 * consumer's own keydown listeners: one that already used Escape (an open
 * suggestion list calls `preventDefault`) keeps it. The key itself is never
 * prevented, so a table cell or a dialog still sees it and moves on.
 *
 * - Lazy model: nothing typed is committed before Enter, Tab or blur, so the
 *   stored model still holds the value from focus — show it again and commit
 *   nothing. The following `change` finds the field unchanged.
 * - Plain model: every keystroke was emitted already, so the value from
 *   before the first edit is shown and emitted back once; field and host
 *   model are as before the edit.
 */
function onEscapeKeydown(event: KeyboardEvent) {
  if (event.key !== 'Escape' || event.defaultPrevented || event.isComposing) return;
  if (!props.escapeRestores) return;
  const el = inputRef.value;
  if (valueModelModifiers.lazy) {
    const committed = valueModel.value ?? '';
    if (el && el.value !== committed) el.value = committed;
  } else {
    const start = editStart;
    if (start === null) return;
    editStart = null;
    if (el && el.value !== start) el.value = start;
    // The model setter emits only when the host holds something else (also
    // when the host has not caught up with the typing yet).
    valueModel.value = start;
  }
  // Chromium clears a kit `search`-typed field after this keydown; its guard
  // must hand back the restored value, not the discarded typing.
  if (el) rearmSearchEscapeGuard(el);
}
const wrapperRef = useTemplateRef<{ wrapperElement: HTMLElement | null }>('wrapper');
defineExpose({
  inputElement: inputRef,
  wrapperElement: computed(() => wrapperRef.value?.wrapperElement ?? null),
});
</script>

<template>
  <NxInputWrapper ref="wrapper" v-bind="wrapperProps">
    <template #default="{ inputAttrs, textHidden }">
      <input
        v-if="valueModelModifiers.lazy"
        ref="input-element"
        v-bind="{
          ...inputAttrs,
          ...autofillAttrs,
          ...$attrs,
          ...autofillWins,
          style: { ...inputAttrs.style, ...($attrs.style as {}) },
        }"
        v-model.lazy="valueModel"
        :class="[inputClass, { 'text-hidden': textHidden }]"
        @keydown="onEscapeKeydown"
      />
      <input
        v-else
        ref="input-element"
        v-bind="{
          ...inputAttrs,
          ...autofillAttrs,
          ...$attrs,
          ...autofillWins,
          style: { ...inputAttrs.style, ...($attrs.style as {}) },
        }"
        v-model="valueModel"
        :class="[inputClass, { 'text-hidden': textHidden }]"
        @focus="onFocus"
        @keydown.capture="rememberEditStart"
        @beforeinput="rememberEditStart"
        @change="onChange"
        @keydown="onEscapeKeydown"
      />
    </template>
  </NxInputWrapper>
</template>

<style scoped src="./style.css" />
<style scoped>
/* `type="search"` only keeps browser autofill out (browserAutofill.ts): it must
   look and behave like a text field — no search styling, no clear button. */
input[type='search'] {
  appearance: none;
}

input[type='search']::-webkit-search-cancel-button,
input[type='search']::-webkit-search-decoration {
  appearance: none;
  display: none;
}

.text-hidden {
  color: transparent;
  user-select: none;
  pointer-events: none;
}

/* `align="end"` (NxTextInput, NxNumberInput, NxCurrencyInput): the value sits
   on the field's end edge. Only the text moves — icons, deco and actions keep
   their places. Without the prop nothing is set here, so an NxTable
   `align="end"` cell keeps aligning its edit field itself. */
input.align-end {
  text-align: end;
}

/* `uppercase` (NxTextInput): the value shows in capitals while it is typed.
   Only the look — the model keeps what was typed; a consumer that stores
   capitals converts the value itself. */
input.uppercase {
  text-transform: uppercase;
}
</style>
